import {HttpErrorResponse} from '@angular/common/http';
import {throwError} from 'rxjs';
import * as UrlAssembler from 'url-assembler';
import {environment} from '../../../environments/environment';
import {Product} from '../../products/models/product';
import {Address, Quote, QuoteItem, QuoteRequest, QuoteState} from '../model/quote';
import {ProductEntity, QuoteAddressEntity, QuoteEntity, QuoteRequestEntity, QuotesEntity} from '../model/quote-entity';
import {QuotesError, QuotesErrors} from '../model/quotes-error';

export const QuotesResources = {
    quotes: '/V1/quotes/',
    quote: '/V1/quotes/:quoteId',
    quoteProducts: '/V1/quotes/:quoteId/products',
    quoteProduct: '/V1/quotes/:quoteId/products/:productId',
    quoteRequest: '/V1/quotes/:quoteId/request',
    quoteDownload: '/V1/quotes/:quoteId/download'
};

export const quotesErrorSelector = (err: any, errorCode: QuotesErrors = QuotesErrors.UnKnown, message: string = '') => {
    if (err.constructor.name === HttpErrorResponse.name) {
        const error = <HttpErrorResponse>err;
        if (error.error && error.error.message) {
            message = error.error.message;
        }
    }

    return throwError(new QuotesError(
        errorCode,
        message,
        err
    ));
};

export class AbstractQuoteService {
    private quotesUrlAssembler;
    private quoteUrlAssembler;
    private quoteProductsUrlAssembler;
    private quoteProductUrlAssembler;
    private quoteRequestUrlAssembler;

    private quoteDownloadUrlAssembler;

    constructor() {
        const base = UrlAssembler(environment.rest_backend);

        this.quotesUrlAssembler = base.segment(QuotesResources.quotes);
        this.quoteUrlAssembler = base.segment(QuotesResources.quote);
        this.quoteProductsUrlAssembler = base.segment(QuotesResources.quoteProducts);
        this.quoteProductUrlAssembler = base.segment(QuotesResources.quoteProduct);
        this.quoteRequestUrlAssembler = base.segment(QuotesResources.quoteRequest);
        this.quoteDownloadUrlAssembler = base.segment(QuotesResources.quoteDownload);
    }

    protected determineQuotesResourceUrl(): string {
        return this.quotesUrlAssembler.toString();
    }

    protected determineQuoteResourceUrl(quote: Quote): string {
        return this.quoteUrlAssembler
            .param({
                'quoteId': quote.id
            })
            .toString();
    }

    protected determineQuoteProductsResourceUrl(quote: Quote): string {
        return this.quoteProductsUrlAssembler
            .param({
                'quoteId': quote.id
            })
            .toString();
    }

    protected determineQuoteProductResourceUrl(quote: Quote, product: Product): string {
        return this.quoteProductUrlAssembler
            .param({
                'quoteId': quote.id,
                'productId': product.id
            })
            .toString();
    }

    protected determineQuoteRequestResourceUrl(quote: Quote): string {
        return this.quoteRequestUrlAssembler
            .param({
                'quoteId': quote.id
            })
            .toString();
    }

    protected determineQuoteDownloadResourceUrl(quote: Quote): string {
        return this.quoteDownloadUrlAssembler
            .param({
                'quoteId': quote.id
            })
            .toString();
    }

    protected convertToQuoteItem(productEntity: ProductEntity): QuoteItem {
        return {
            quantity: productEntity.quantity,
            productId: productEntity.product_id
        };
    }

    protected convertToQuote(quoteEntity: QuoteEntity): Quote {
        return {
            id: quoteEntity.quote_id,
            name: quoteEntity.name,
            creationDate: this.createDateAsUTC(quoteEntity.creation_date),
            state: QuoteState[quoteEntity.state],
            quoteItems: quoteEntity.products.map(product => this.convertToQuoteItem(product)),
            request: this.convertToQuoteRequest(quoteEntity.request_data)
        };
    }

    protected convertToQuoteRequest(quoteRequestEntity: QuoteRequestEntity): QuoteRequest {
        if (quoteRequestEntity == null) {
            return null;
        }

        return {
            generatedRequestId: quoteRequestEntity.generated_request_id,
            requestDate: this.createDateAsUTC(quoteRequestEntity.request_date),
            address: this.convertToAddress(quoteRequestEntity.address)
        };
    }

    protected convertToAddress(quoteAddressEntity: QuoteAddressEntity): Address {
        return {
            city: quoteAddressEntity.city,
            company: quoteAddressEntity.company,
            countryId: quoteAddressEntity.country_id,
            firstName: quoteAddressEntity.first_name,
            lastName: quoteAddressEntity.last_name,
            street: quoteAddressEntity.street,
            vat: quoteAddressEntity.vat,
            zipCode: quoteAddressEntity.zip_code
        };
    }

    private createDateAsUTC(creation_date: string) {
        const date = new Date(Date.parse(creation_date.replace(/-/g, '/')));
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(),
            date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

    protected convertToQuotes(quotesEntity: QuotesEntity): Quote[] {
        console.log('convert quotes entity to quote array', quotesEntity);

        const quoteEntities = quotesEntity.quotes;
        if (quoteEntities == null || quoteEntities.length === 0) {
            return [];
        }

        return quotesEntity.quotes.map(quoteEntity => this.convertToQuote(quoteEntity));
    }
}
